import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { KintoneSystem } from "~/functions/api/kintoneSystem";
import { KintoneSystemApi } from "~/functions/api/kintoneSystem";
import type { RootState } from "~/store";

const FALLBACK_TIMEOUTS = [180_000, 240_000, 300_000] as const;

const ACTIVE_STATUSES = new Set<KintoneSystem["status"]>(["queued", "running"]);
const TERMINAL_STATUSES = new Set<KintoneSystem["status"]>(["succeeded", "failed", "canceled"]);

interface UseAppGenerationFallbackParams {
  system: KintoneSystem | null;
  systemId: string | undefined;
  onUpdate: (system: KintoneSystem) => void;
}

export const useAppGenerationFallback = ({
  system,
  systemId,
  onUpdate,
}: UseAppGenerationFallbackParams): void => {
  const { currentProject, currentUser } = useSelector((state: RootState) => state.auth);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptRef = useRef(0);
  // APIが失敗してsystemが変わらなくてもeffectを再実行するためのカウンター
  const [retryTrigger, setRetryTrigger] = useState(0);

  const kintoneSystemApi = useRef(new KintoneSystemApi());

  useEffect(() => {
    const isActive = system ? ACTIVE_STATUSES.has(system.status) : false;

    if (!isActive) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      attemptRef.current = 0;
      return;
    }

    if (timerRef.current) return;

    const attempt = attemptRef.current;
    if (attempt >= FALLBACK_TIMEOUTS.length) return;

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      attemptRef.current += 1;
      console.warn(
        `[useAppGenerationFallback] WebPubSub fallback triggered (attempt ${attempt + 1}/${FALLBACK_TIMEOUTS.length})`,
      );

      const organizationId = currentUser?.organization_id;
      const projectId = currentProject?.id;

      if (!organizationId || !projectId || !systemId) {
        setRetryTrigger((prev) => prev + 1);
        return;
      }

      kintoneSystemApi.current
        .getKintoneSystem(organizationId, projectId, systemId)
        .then((fetched) => {
          if (!fetched) return;

          const statusChanged = fetched.status !== system?.status;
          const logsChanged = JSON.stringify(fetched.logs) !== JSON.stringify(system?.logs);

          if (statusChanged || logsChanged) {
            onUpdate(fetched);
          }

          if (TERMINAL_STATUSES.has(fetched.status)) {
            attemptRef.current = FALLBACK_TIMEOUTS.length;
          }
        })
        .catch((error) => {
          console.error("[useAppGenerationFallback] Failed to fetch system:", error);
        })
        .finally(() => {
          // systemが変わらなくても次のタイマーをスケジュールするためeffectを再トリガー
          setRetryTrigger((prev) => prev + 1);
        });
    }, FALLBACK_TIMEOUTS[attempt]);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [system, systemId, currentUser?.organization_id, currentProject?.id, onUpdate, retryTrigger]);
};
